import axios from "axios";
import dayjs from "dayjs";

class Api {
  url = process.env.REACT_APP_API_URL;
  apiCustomer = process.env.REACT_APP_API_CUSTOMER;
  apiKey = process.env.REACT_APP_API_KEY;

  async getClient(): Promise<any> {
    const headers = this._getHeaders();
    try {
      const result = await axios.get(`${this.url}/clients/me`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async getWebhookLogs(page: number, pageSize: number): Promise<any> {
    const headers = this._getHeaders();
    try {
      const result = await axios.post(
        `${this.url}/clients/webhook-logs`,
        {
          page,
          pageSize,
        },
        {
          headers: { ...headers },
        }
      );
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async getUsers(): Promise<any> {
    const headers = this._getHeaders();
    try {
      const result = await axios.get(`${this.url}/users`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async createWeightGoal(
    externalId: string,
    data: {
      weightGoal: number;
      fromDate: string;
      toDate: string;
    }
  ): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.post(
        `${this.url}/users/${externalId}/weight-goals`,
        data,
        { headers }
      );
      return result.data;
    } catch (ex) {
      throw ex;
    }
  }

  async getWeightGoals(externalId: string): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.get(`${this.url}/users/${externalId}/weight-goals`, { headers });
      return result.data;
    } catch (ex) {
      throw ex;
    }
  }

  async getResource(resourceId: string): Promise<any> {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/graphs/${resourceId}`
      );

      return response.data;
    } catch (ex) {
      return undefined;
    }
  }

  async getHeartIntraday(
    userId: string,
    brandId: number,
    date: string
  ): Promise<any> {
    const headers = this._getHeaders();
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/users/${userId}/intraday-parameters/heart?brand_id=${brandId}&date=${date}`,
        { headers }
      );

      return response.data;
    } catch (ex) {
      return undefined;
    }
  }

  async getAggregatedData(parameterCatalogIds: number[]): Promise<any> {
    const headers = this._getHeaders();
    console.log("here", headers);
    const today = new Date();
    const oneWeekAgo = dayjs(today).subtract(14, "day").toDate();

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/clients/aggregated-parameters`,
        {
          startDate: oneWeekAgo.toISOString().split("T")[0],
          endDate: today.toISOString().split("T")[0],
          companyId: null,
          parameterCatalogIds: parameterCatalogIds,
          units: "vivanta_standard",
        },
        { headers }
      );

      return response.data;
    } catch (ex) {
      return undefined;
    }
  }

  _getHeaders(): any {
    return {
      "ngrok-skip-browser-warning": "true",
      "x-api-key": localStorage.getItem("publicKey"),
      "x-customer": localStorage.getItem("externalId"),
    };
  }

  __getClinicalHistoryHeaders(): any {
    const token = localStorage.getItem("staffToken");
    return {
      "ngrok-skip-browser-warning": "true",
      "Authorization": token ? `Bearer ${token}` : "",
      "x-api-key": this.apiKey,
      "x-customer": this.apiCustomer,
      "Content-Type": "application/json",
    };
  }

  async login(username: string, password: string): Promise<any> {
    try {
      const result = await axios.post(
        `${this.url}/staff/login`,
        {
          username,
          password,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      
      if (result.data && result.data.token) {
        localStorage.setItem("staffToken", result.data.token);
      }
      
      return result.data;
    } catch (ex) {
      throw ex;
    }
  }

  async webhookTest(payload: string): Promise<any> {
    const headers = this._getHeaders();
    return await axios.get(`${process.env.REACT_APP_API_URL}/${payload}`, {
      headers,
    });
  }

  async getHealthFairs(): Promise<any> {
   const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.get(`${this.url}/health-fairs`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async getHealthFairParticipants(uuid: string): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      console.log('api call');
      const result = await axios.get(`${this.url}/health-fairs/${uuid}/participants`, {
        headers: { ...headers },
      });
      console.log('result', result.data);
      return result.data.map((participant: any) => {
        let bodyMassIndex = participant.weightKg && participant.heightCm / 100 ? +participant.weightKg / ((+participant.heightCm / 100) * (+participant.heightCm / 100)): 0;
        let waistHeightRatio = participant.waistCm && participant.heightCm ? +participant.waistCm / +participant.heightCm : 0;
        // only two decimals in those numbers
        bodyMassIndex = parseFloat(bodyMassIndex.toFixed(2));
        waistHeightRatio = parseFloat(waistHeightRatio.toFixed(2));
        return {
        ...participant,
        bodyMassIndex: bodyMassIndex,
        waistHeightRatio: waistHeightRatio,
      }
      });
    } catch (ex) {
      console.log('error', ex);
      return undefined;
    }
  }

  async getClinicalHistory(userExternalId: string): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.get(`${this.url}/clinical-histories/${userExternalId}`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async getActiveSubstanceAllergies(clinicalHistoryId: number): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.get(`${this.url}/clinical-histories/${clinicalHistoryId}/active-substance-allergies`, {
        headers: { ...headers },
      });
      console.log('allergies', result.data);
      console.log('url', `${this.url}/clinical-histories/${clinicalHistoryId}/active-substance-allergies`);
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async getActiveSubstanceCurrentTreatments(clinicalHistoryId: number): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.get(`${this.url}/clinical-histories/${clinicalHistoryId}/active-substance-current-treatments`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async getCurrentDiseases(clinicalHistoryId: number): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.get(`${this.url}/clinical-histories/${clinicalHistoryId}/current-diseases`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async getProcedures(clinicalHistoryId: number): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.get(`${this.url}/clinical-histories/${clinicalHistoryId}/procedures`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async getOtherAllergies(clinicalHistoryId: number): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.get(`${this.url}/clinical-histories/${clinicalHistoryId}/other-allergies`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async getFoodAllergies(clinicalHistoryId: number): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.get(`${this.url}/clinical-histories/${clinicalHistoryId}/food-allergies`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async getHeredofamiliarDiseases(clinicalHistoryId: number): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.get(`${this.url}/clinical-histories/${clinicalHistoryId}/heredofamiliar-diseases`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async getAllAllergens(): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.get(`${this.url}/allergens/all`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async searchOtherAllergies(name: string): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.get(`${this.url}/allergens/all?name=${encodeURIComponent(name)}`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async createOtherAllergy(clinicalHistoryId: number, allergenId: number): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.post(`${this.url}/clinical-histories/${clinicalHistoryId}/other-allergies`, {
        clinicalHistoryId,
        allergenId,
      }, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  // Active Substances
  async getAllActiveSubstances(): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.get(`${this.url}/active-substances/all`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async searchActiveSubstances(name: string): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.get(`${this.url}/active-substances/all?name=${encodeURIComponent(name)}`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async createActiveSubstanceAllergy(clinicalHistoryId: number, activeSubstanceId: number): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.post(`${this.url}/clinical-histories/${clinicalHistoryId}/active-substance-allergies`, {
        clinicalHistoryId,
        activeSubstanceId,
      }, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async createActiveSubstanceCurrentTreatment(clinicalHistoryId: number, activeSubstanceId: number): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.post(`${this.url}/clinical-histories/${clinicalHistoryId}/active-substance-current-treatments`, {
        clinicalHistoryId,
        activeSubstanceId,
      }, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async createCurrentDisease(clinicalHistoryId: number, activeSubstanceId: number, notes: string | null): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.post(`${this.url}/clinical-histories/${clinicalHistoryId}/current-diseases`, {
        clinicalHistoryId,
        activeSubstanceId,
        notes,
      }, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  // Foods
  async getAllFoods(): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.get(`${this.url}/foods/all`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async searchFoods(name: string): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.get(`${this.url}/foods/all?name=${encodeURIComponent(name)}`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async createFoodAllergy(clinicalHistoryId: number, foodId: number): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.post(`${this.url}/clinical-histories/${clinicalHistoryId}/food-allergies`, {
        clinicalHistoryId,
        foodId,
      }, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  // CIE9 (Procedures)
  async getAllCIE9(): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.get(`${this.url}/cie9s/all`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async searchCIE9(name: string): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.get(`${this.url}/cie9/all?name=${encodeURIComponent(name)}`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async createProcedure(clinicalHistoryId: number, cie9Id: number, dateOfProcedure: string): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.post(`${this.url}/clinical-histories/${clinicalHistoryId}/procedures`, {
        clinicalHistoryId,
        cie9Id,
        dateOfProcedure,
      }, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  // CIE10 (Heredofamiliar Diseases)
  async getAllCIE10(): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.get(`${this.url}/cie10/all`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async searchCIE10(name: string): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.get(`${this.url}/cie10s/all?name=${encodeURIComponent(name)}`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async createHeredofamiliarDisease(
    clinicalHistoryId: number,
    cie10Id: number,
    numberOfChildren: number,
    isPaternalGrandfatherDiseased: number,
    isPaternalGrandmotherDiseased: number,
    isMaternalGrandfatherDiseased: number,
    isMaternalGrandmotherDiseased: number,
    numberOfSiblings: number
  ): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.post(`${this.url}/clinical-histories/${clinicalHistoryId}/heredofamiliar-diseases`, {
        clinicalHistoryId,
        cie10Id,
        numberOfChildren,
        isPaternalGrandfatherDiseased,
        isPaternalGrandmotherDiseased,
        isMaternalGrandfatherDiseased,
        isMaternalGrandmotherDiseased,
        numberOfSiblings,
      }, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  // Delete methods
  async deleteActiveSubstanceAllergy(clinicalHistoryId: number, id: number): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.delete(`${this.url}/clinical-histories/${clinicalHistoryId}/active-substance-allergies/${id}`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async deleteActiveSubstanceCurrentTreatment(clinicalHistoryId: number, id: number): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.delete(`${this.url}/clinical-histories/${clinicalHistoryId}/active-substance-current-treatments/${id}`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async deleteCurrentDisease(clinicalHistoryId: number, id: number): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.delete(`${this.url}/clinical-histories/${clinicalHistoryId}/current-diseases/${id}`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async deleteProcedure(clinicalHistoryId: number, id: number): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.delete(`${this.url}/clinical-histories/${clinicalHistoryId}/procedures/${id}`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async deleteFoodAllergy(clinicalHistoryId: number, id: number): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.delete(`${this.url}/clinical-histories/${clinicalHistoryId}/food-allergies/${id}`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async deleteOtherAllergy(clinicalHistoryId: number, id: number): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.delete(`${this.url}/clinical-histories/${clinicalHistoryId}/other-allergies/${id}`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async deleteHeredofamiliarDisease(clinicalHistoryId: number, id: number): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.delete(`${this.url}/clinical-histories/${clinicalHistoryId}/heredofamiliar-diseases/${id}`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async updateClinicalHistory(clinicalHistoryId: number, data: any): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.patch(`${this.url}/clinical-histories/${clinicalHistoryId}`, data, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  // Vivanta Treatments API Methods

  // Active Substance Units
  async getActiveSubstanceUnits(): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.get(`${this.url}/vivanta-treatments/active-substance-units`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async getActiveSubstanceUnitById(id: number): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.get(`${this.url}/vivanta-treatments/active-substance-units/${id}`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async createActiveSubstanceUnit(data: { name: string; abbreviation: string }): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.post(`${this.url}/vivanta-treatments/active-substance-units`, data, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      throw ex;
    }
  }

  async updateActiveSubstanceUnit(id: number, data: { name?: string; abbreviation?: string }): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.patch(`${this.url}/vivanta-treatments/active-substance-units/${id}`, data, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      throw ex;
    }
  }

  async deleteActiveSubstanceUnit(id: number): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.delete(`${this.url}/vivanta-treatments/active-substance-units/${id}`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      throw ex;
    }
  }

  // Time Interval Units
  async getTimeIntervalUnits(): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.get(`${this.url}/vivanta-treatments/time-interval-units`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async getTimeIntervalUnitById(id: number): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.get(`${this.url}/vivanta-treatments/time-interval-units/${id}`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async createTimeIntervalUnit(data: { name: string; hoursDuration: number }): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.post(`${this.url}/vivanta-treatments/time-interval-units`, data, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      throw ex;
    }
  }

  async updateTimeIntervalUnit(id: number, data: { name?: string; hoursDuration?: number }): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.patch(`${this.url}/vivanta-treatments/time-interval-units/${id}`, data, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      throw ex;
    }
  }

  async deleteTimeIntervalUnit(id: number): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.delete(`${this.url}/vivanta-treatments/time-interval-units/${id}`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      throw ex;
    }
  }

  // Treatment Periodicities
  async getTreatmentPeriodicities(): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.get(`${this.url}/vivanta-treatments/treatment-periodicities`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async getTreatmentPeriodicityById(id: number): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.get(`${this.url}/vivanta-treatments/treatment-periodicities/${id}`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async createTreatmentPeriodicity(data: { timeInterval: number; timeIntervalUnitId: number }): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.post(`${this.url}/vivanta-treatments/treatment-periodicities`, data, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      throw ex;
    }
  }

  async updateTreatmentPeriodicity(id: number, data: { timeInterval?: number; timeIntervalUnitId?: number }): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.patch(`${this.url}/vivanta-treatments/treatment-periodicities/${id}`, data, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      throw ex;
    }
  }

  async deleteTreatmentPeriodicity(id: number): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.delete(`${this.url}/vivanta-treatments/treatment-periodicities/${id}`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      throw ex;
    }
  }

  // Medications
  async getMedications(): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.get(`${this.url}/vivanta-treatments/medications`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async getMedicationById(id: number): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.get(`${this.url}/vivanta-treatments/medications/${id}`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async createMedication(data: { name: string; presentation?: string }): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.post(`${this.url}/vivanta-treatments/medications`, data, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      throw ex;
    }
  }

  async updateMedication(id: number, data: { name?: string; presentation?: string }): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.patch(`${this.url}/vivanta-treatments/medications/${id}`, data, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      throw ex;
    }
  }

  async deleteMedication(id: number): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.delete(`${this.url}/vivanta-treatments/medications/${id}`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      throw ex;
    }
  }

  // Medication Active Substances
  async getMedicationActiveSubstances(): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.get(`${this.url}/vivanta-treatments/medication-active-substances`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async getMedicationActiveSubstanceById(id: number): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.get(`${this.url}/vivanta-treatments/medication-active-substances/${id}`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async createMedicationActiveSubstance(data: { medicationId: number; activeSubstanceId: number; activeSubstanceUnitId: number; quantity: number }): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.post(`${this.url}/vivanta-treatments/medication-active-substances`, data, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      throw ex;
    }
  }

  async updateMedicationActiveSubstance(id: number, data: { medicationId?: number; activeSubstanceId?: number; activeSubstanceUnitId?: number; quantity?: number }): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.patch(`${this.url}/vivanta-treatments/medication-active-substances/${id}`, data, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      throw ex;
    }
  }

  async deleteMedicationActiveSubstance(id: number): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.delete(`${this.url}/vivanta-treatments/medication-active-substances/${id}`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      throw ex;
    }
  }

  // Treatments
  async getTreatments(): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.get(`${this.url}/vivanta-treatments`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async getTreatmentById(id: number): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.get(`${this.url}/vivanta-treatments/${id}`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async createTreatment(data: {
    name: string;
    medicationId: number;
    treatmentPeriodicityId: number;
    routeOfAdministration: string;
    treatmentDurationDays: number;
    isTemplate: boolean;
    notes?: string;
    takeQuantity?: number | null;
  }): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.post(`${this.url}/vivanta-treatments`, data, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      throw ex;
    }
  }

  async updateTreatment(id: number, data: {
    name?: string;
    medicationId?: number;
    treatmentPeriodicityId?: number;
    routeOfAdministration?: string;
    treatmentDurationDays?: number;
    isTemplate?: boolean;
    notes?: string;
    takeQuantity?: number | null;
  }): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.patch(`${this.url}/vivanta-treatments/${id}`, data, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      throw ex;
    }
  }

  async deleteTreatment(id: number): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.delete(`${this.url}/vivanta-treatments/${id}`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      throw ex;
    }
  }

  async getTreatmentReminders(treatmentId: number): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.get(`${this.url}/vivanta-treatments/${treatmentId}/reminders`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async createTreatmentReminder(data: {
    treatmentId: number;
    hour: number;
    minute: number;
  }): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.post(`${this.url}/vivanta-treatments/reminders`, data, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      throw ex;
    }
  }

  // User Vivanta Treatments
  async getUserVivantaTreatments(userExternalId: string): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.get(`${this.url}/users/${userExternalId}/vivanta-treatments`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async createUserVivantaTreatment(data: {
    userId: number;
    vivantaTreatmentId: number;
    startDate: string;
    endDate: string;
    isActive: boolean;
    notes?: string;
  }): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.post(`${this.url}/vivanta-treatments/user-vivanta-treatments`, data, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      throw ex;
    }
  }

  async deleteUserVivantaTreatment(id: number): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.delete(`${this.url}/vivanta-treatments/user-vivanta-treatments/${id}`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      throw ex;
    }
  }

  async getPrograms(): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.get(`${this.url}/vivanta-programs`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async addPatientMorbidity(programId: string, externalId: string, morbidity: string): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.post(
        `${this.url}/vivanta-programs/${programId}/users/${externalId}/morbidities`,
        { morbidity, startDateOfDisease: new Date().toISOString().slice(0, 10) },
        { headers: { ...headers } },
      );
      return result.data;
    } catch (ex) {
      throw ex;
    }
  }

  async getPatientHealthFairLabs(programId: string, externalId: string): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.get(`${this.url}/vivanta-programs/${programId}/users/${externalId}`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }

  async getProgramDashboard(programId: string): Promise<any> {
    const headers = this.__getClinicalHistoryHeaders();
    try {
      const result = await axios.get(`${this.url}/vivanta-programs/${programId}/users`, {
        headers: { ...headers },
      });
      return result.data;
    } catch (ex) {
      return undefined;
    }
  }
}



export default Api;
